import { useState } from 'react';

type Forca = 'fraca' | 'media' | 'boa';

function calcularForca(senha: string): Forca | null {
  if (!senha) return null;
  if (senha.length < 6) return 'fraca';

  let variedade = 0;
  if (/[a-z]/.test(senha)) variedade += 1;
  if (/[A-Z]/.test(senha)) variedade += 1;
  if (/[0-9]/.test(senha)) variedade += 1;
  if (/[^a-zA-Z0-9]/.test(senha)) variedade += 1;

  if (senha.length >= 8 && variedade >= 3) return 'boa';
  if (variedade >= 2) return 'media';
  return 'fraca';
}

const FORCA_INFO: Record<Forca, { rotulo: string; cor: string; texto: string; segmentos: number }> = {
  fraca: { rotulo: 'Fraca', cor: 'bg-danger-500', texto: 'text-danger-600', segmentos: 1 },
  media: { rotulo: 'Média', cor: 'bg-amber-500', texto: 'text-amber-600', segmentos: 2 },
  boa: { rotulo: 'Boa', cor: 'bg-accent-500', texto: 'text-accent-600', segmentos: 3 },
};

interface CampoSenhaProps {
  id: string;
  name: string;
  label: string;
  help?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string | null;
  autoComplete?: string;
  mostrarForca?: boolean;
}

export default function CampoSenha({
  id,
  name,
  label,
  help,
  value,
  onChange,
  required,
  error,
  autoComplete,
  mostrarForca = false,
}: CampoSenhaProps) {
  const [visivel, setVisivel] = useState(false);
  const forca = mostrarForca ? calcularForca(value) : null;
  const info = forca ? FORCA_INFO[forca] : null;

  return (
    <div>
      <label htmlFor={id} className="block text-base font-medium text-foreground-950">
        {label} {required && <span className="text-danger-600">*</span>}
      </label>
      {help && <p className="mt-1 text-sm text-secondary-500">{help}</p>}

      <div className="relative mt-1">
        <input
          id={id}
          name={name}
          type={visivel ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className={`w-full border-0 border-b bg-transparent px-0 py-2 pr-10 text-[15px] text-foreground-950 outline-none transition-colors placeholder:text-secondary-400 focus:border-b-2 focus:border-primary-500 ${
            error ? 'border-danger-500' : 'border-secondary-300'
          }`}
        />
        <button
          type="button"
          onClick={() => setVisivel((v) => !v)}
          aria-label={visivel ? 'Esconder senha' : 'Mostrar senha'}
          className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-secondary-400 transition-colors hover:text-secondary-600"
        >
          <i className={visivel ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'} />
        </button>
      </div>

      {forca && info && (
        <div className="mt-2">
          <div className="flex gap-1">
            <div className={`h-1 flex-1 rounded-full ${info.segmentos >= 1 ? info.cor : 'bg-secondary-200'}`} />
            <div className={`h-1 flex-1 rounded-full ${info.segmentos >= 2 ? info.cor : 'bg-secondary-200'}`} />
            <div className={`h-1 flex-1 rounded-full ${info.segmentos >= 3 ? info.cor : 'bg-secondary-200'}`} />
          </div>
          <p className={`mt-1 text-xs ${info.texto}`}>{info.rotulo}</p>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
          {error}
        </p>
      )}
    </div>
  );
}